import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import { makeStyles } from "@material-ui/core/styles";
import { P, NETWORK } from "./constants";
import { Card } from "./ui";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: 12 },

  // Dark indigo header banner
  banner: { background: P.nv, borderRadius: 10, padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr auto", gap: 20, alignItems: "center" },
  bannerHeading: { fontWeight: 600, letterSpacing: ".08em", color: "rgba(255,255,255,0.5)", marginBottom: 6 },
  rankRow: { display: "flex", gap: 24 },
  rankLabel: { color: "rgba(255,255,255,0.45)", marginBottom: 3 },
  rankValue: { fontWeight: 800, color: "#fff" },
  tilesRow: { display: "flex", gap: 16 },
  tile: { textAlign: "center" },
  tileLabel: { color: "rgba(255,255,255,0.45)", marginBottom: 2 },
  tileValue: { fontWeight: 700, color: "#fff" },

  // Move-up action cards
  moveCard: { display: "grid", gridTemplateColumns: "4px 1fr", borderRadius: 8, overflow: "hidden", marginBottom: 8, border: "0.5px solid #BFE3C6" },
  moveStrip: { background: "#241C8A" },
  moveBody: { padding: "10px 14px", background: "#E6F4E8" },
  moveHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 },
  moveMetric: { fontWeight: 700, color: "#2E7D32", letterSpacing: ".04em" },
  moveTargets: { display: "flex", gap: 8, alignItems: "center", flexShrink: 0, marginLeft: 12 },
  moveYou: { color: "var(--color-text-secondary)" },
  moveYouVal: { fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },
  moveArrow: { color: "#3127B8" },
  moveTarget: { color: "var(--color-text-secondary)" },
  moveTargetVal: { fontWeight: 700, color: "#241C8A", fontFamily: "var(--font-mono)" },
  moveAction: { color: "#2E7D32" },

  // Toolbar
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  rankToggleGroup: { display: "flex", gap: 2, background: "var(--color-background-secondary)", borderRadius: 7, padding: 3, border: "0.5px solid var(--color-border-tertiary)" },
  rankToggleBtn: { textTransform: "none", minWidth: 0, padding: "5px 16px", borderRadius: 5 },
  colToggleRow: { display: "flex", gap: 5, alignItems: "center" },
  showLabel: { color: "var(--color-text-tertiary)", marginRight: 2 },
  colToggleBtn: { textTransform: "none", minWidth: 0, padding: "3px 10px", borderRadius: 4 },

  // Table
  table: { width: "100%" },
  th: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    padding: "5px 10px",
    borderBottom: "0.5px solid var(--color-border-tertiary)",
    letterSpacing: ".04em",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 10px",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--color-text-primary)",
    borderBottom: "none",
  },
  right: { textAlign: "right" },
  mono: { fontFamily: "var(--font-mono)" },
  row: { borderBottom: "0.5px solid var(--color-border-tertiary)" },
  youBadge: { marginLeft: 6, fontSize: 12, fontWeight: 700, color: "#fff", background: P.nv, padding: "1px 5px", borderRadius: 3 },
});

export default function NetworkTab({ period }) {
  const cls = useStyles();
  const [rankBy, setRankBy] = useState("overall");
  const [cols, setCols] = useState({dc:true, foc:true, bo:true, parkOcc:true, upsells:true});
  const toggleCol = k => setCols(prev=>({...prev,[k]:!prev[k]}));

  const me = NETWORK.find(h=>h.me);

  const grouped = ["Accor","Marriott","IHG","EVT","TFE","Hilton","Hyatt","Wyndham","Radisson","Oaks"].map(g=>{
    const hotels=NETWORK.filter(h=>h.group===g);
    const n=hotels.length;
    return {group:g,count:n,myGroup:hotels.some(h=>h.me),
      avgDc:Math.round(hotels.reduce((a,h)=>a+h.dc,0)/n),
      avgFoc:+(hotels.reduce((a,h)=>a+h.foc,0)/n).toFixed(1),
      avgBo:+(hotels.reduce((a,h)=>a+h.bo,0)/n).toFixed(1),
      avgParkOcc:Math.round(hotels.reduce((a,h)=>a+h.parkOcc,0)/n),
      avgUpsells:Math.round(hotels.reduce((a,h)=>a+h.upsells,0)/n),
    };
  }).sort((a,b)=>b.avgDc-a.avgDc);

  const byState = ["NSW","VIC","QLD","WA","SA","TAS","NT","ACT"].map(s=>{
    const hotels=NETWORK.filter(h=>h.state===s); const n=hotels.length;
    const myRank=hotels.sort((a,b)=>a.rank-b.rank).findIndex(h=>h.me)+1;
    return {state:s,count:n,myState:hotels.some(h=>h.me),myRank,
      avgDc:Math.round(hotels.reduce((a,h)=>a+h.dc,0)/n),
      avgFoc:+(hotels.reduce((a,h)=>a+h.foc,0)/n).toFixed(1),
      avgBo:+(hotels.reduce((a,h)=>a+h.bo,0)/n).toFixed(1),
      avgParkOcc:Math.round(hotels.reduce((a,h)=>a+h.parkOcc,0)/n),
      avgUpsells:Math.round(hotels.reduce((a,h)=>a+h.upsells,0)/n),
    };
  }).sort((a,b)=>b.avgDc-a.avgDc);

  const sortedOverall = [...NETWORK].sort((a,b)=>a.rank-b.rank);
  const nextAbove = sortedOverall[sortedOverall.findIndex(h=>h.me)-1];

  const COLS = [
    {key:"dc",      label:"DRIVO %",    me:me.dc+"%",      fmt:v=>v+"%",      better:"higher", gap: nextAbove?nextAbove.dc-me.dc:0},
    {key:"foc",     label:"FOC Ratio",  me:me.foc+"%",     fmt:v=>v+"%",      better:"lower",  gap: nextAbove?me.foc-nextAbove.foc:0},
    {key:"bo",      label:"Buzz-out %", me:me.bo+"%",      fmt:v=>v+"%",      better:"lower",  gap: nextAbove?me.bo-nextAbove.bo:0},
    {key:"parkOcc", label:"Parking Occ",me:me.parkOcc+"%", fmt:v=>v+"%",      better:"higher", gap: nextAbove?nextAbove.parkOcc-me.parkOcc:0},
    {key:"upsells", label:"Upsells",    me:me.upsells,     fmt:v=>v,          better:"higher", gap: nextAbove?nextAbove.upsells-me.upsells:0},
  ];

  const moveUpActions = [
    nextAbove&&me.dc<nextAbove.dc && {metric:"DRIVO %", current:me.dc+"%", target:nextAbove.dc+"%", action:`Raise DRIVO charge rate by ${nextAbove.dc-me.dc}% -- ensure all vehicles are scanned through DRIVO at exit`},
    nextAbove&&me.foc>nextAbove.foc && {metric:"FOC Ratio", current:me.foc+"%", target:nextAbove.foc+"%", action:`Reduce free-of-charge rate from ${me.foc}% to ${nextAbove.foc}% -- review staff override authorisations`},
    nextAbove&&me.bo>nextAbove.bo && {metric:"Buzz-out %", current:me.bo+"%", target:nextAbove.bo+"%", action:`Cut buzz-outs from ${me.bo}% to ${nextAbove.bo}% -- enforce barrier close process at shift handover`},
    nextAbove&&me.parkOcc<nextAbove.parkOcc && {metric:"Parking Occ", current:me.parkOcc+"%", target:nextAbove.parkOcc+"%", action:`Grow parking occupancy by ${nextAbove.parkOcc-me.parkOcc}% -- activate pre-booking email and in-room QR channels`},
    nextAbove&&me.upsells<nextAbove.upsells && {metric:"Upsells", current:me.upsells, target:nextAbove.upsells, action:`Add ${nextAbove.upsells-me.upsells} more upsells -- enable Canary and QR in-room channels for additional revenue`},
  ].filter(Boolean);

  const myGroupRank = grouped.findIndex(g=>g.myGroup)+1;

  const TH = ({children,right}) => (
    <TableCell className={`${cls.th}${right?" "+cls.right:""}`}>{children}</TableCell>
  );
  const TD = ({children,right,bold,highlight,mono}) => (
    <TableCell
      className={`${cls.td}${right?" "+cls.right:""}${mono?" "+cls.mono:""}`}
      style={{fontWeight:bold?700:undefined,color:highlight?P.nv:undefined}}
    >{children}</TableCell>
  );

  return (
    <Box className={cls.root}>

      <Paper elevation={0} className={cls.banner}>
        <div>
          <Typography variant="body2" component="div" className={cls.bannerHeading}>DRIVO NETWORK -- {NETWORK.length} HOTELS</Typography>
          <Box className={cls.rankRow}>
            {[["Overall Rank","#"+me.rank+" of "+NETWORK.length],["Group ("+me.group+")","#"+myGroupRank+" of "+grouped.length],["State ("+me.state+")","#"+byState.find(s=>s.myState).myRank+" of "+byState.find(s=>s.myState).count]].map(([l,v])=>(
              <div key={l}>
                <Typography variant="caption" component="div" className={cls.rankLabel}>{l}</Typography>
                <Typography variant="h6" component="div" className={cls.rankValue}>{v}</Typography>
              </div>
            ))}
          </Box>
        </div>
        <Box className={cls.tilesRow}>
          {COLS.map(c=>(
            <Box key={c.key} className={cls.tile}>
              <Typography variant="caption" component="div" className={cls.tileLabel}>{c.label}</Typography>
              <Typography variant="subtitle1" component="div" className={cls.tileValue}>{c.me}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {moveUpActions.length>0&&(
        <Card title="To Move Up One Place" subtitle={`Close the gap on rank #${me.rank-1} -- ${period}`}>
          {moveUpActions.map((a,i)=>(
            <Box key={i} className={cls.moveCard}>
              <div className={cls.moveStrip} />
              <Box className={cls.moveBody}>
                <Box className={cls.moveHead}>
                  <Typography variant="body2" component="span" className={cls.moveMetric}>{a.metric.toUpperCase()}</Typography>
                  <Box className={cls.moveTargets}>
                    <Typography variant="caption" component="span" className={cls.moveYou}>You: <span className={cls.moveYouVal}>{a.current}</span></Typography>
                    <Typography variant="body2" component="span" className={cls.moveArrow}>{"→"}</Typography>
                    <Typography variant="caption" component="span" className={cls.moveTarget}>Target: <span className={cls.moveTargetVal}>{a.target}</span></Typography>
                  </Box>
                </Box>
                <Typography variant="body2" component="div" className={cls.moveAction}>{a.action}</Typography>
              </Box>
            </Box>
          ))}
        </Card>
      )}

      <Box className={cls.toolbar}>
        <Box className={cls.rankToggleGroup}>
          {[["overall","Overall"],["group","By Group"],["state","By State"]].map(([id,label])=>(
            <Button key={id} size="small" disableElevation onClick={()=>setRankBy(id)} className={cls.rankToggleBtn} style={{fontWeight:rankBy===id?600:400,color:rankBy===id?"#fff":"var(--color-text-secondary)",background:rankBy===id?P.nv:"transparent"}}>{label}</Button>
          ))}
        </Box>
        <Box className={cls.colToggleRow}>
          <Typography variant="caption" component="span" className={cls.showLabel}>Show:</Typography>
          {COLS.map(c=>(
            <Button key={c.key} size="small" disableElevation onClick={()=>toggleCol(c.key)} className={cls.colToggleBtn} style={{border:"0.5px solid "+(cols[c.key]?"#2E7D32":"var(--color-border-secondary)"),background:cols[c.key]?"#E6F4E8":"transparent",color:cols[c.key]?"#2E7D32":"var(--color-text-tertiary)",fontWeight:cols[c.key]?600:400}}>{c.label}</Button>
          ))}
        </Box>
      </Box>

      {rankBy==="overall"&&(
        <Card title="Overall Network Ranking" subtitle={`${NETWORK.length} hotels -- sorted by overall DRIVO score`}>
          <Table className={cls.table}>
            <TableHead><TableRow>
              <TH>Rank</TH>
              <TH>Group</TH>
              <TH>State</TH>
              {COLS.filter(c=>cols[c.key]).map(c=><TH key={c.key} right>{c.label}</TH>)}
            </TableRow></TableHead>
            <TableBody>{sortedOverall.map((h,i)=>(
              <TableRow key={h.id} className={cls.row} style={{background:h.me?"#E6F4E8":"transparent"}}>
                <TD bold={h.me} highlight={h.me} mono>{h.me?"* "+h.rank:h.rank}</TD>
                <TD bold={h.me} highlight={h.me}>{h.group}{h.me&&<span className={cls.youBadge}>YOU</span>}</TD>
                <TD>{h.state}</TD>
                {COLS.filter(c=>cols[c.key]).map(c=><TD key={c.key} right bold={h.me} highlight={h.me} mono>{c.fmt(h[c.key])}</TD>)}
              </TableRow>
            ))}</TableBody>
          </Table>
        </Card>
      )}

      {rankBy==="group"&&(
        <Card title="Ranking by Group" subtitle="Average metrics per hotel group -- ranked by DRIVO %">
          <Table className={cls.table}>
            <TableHead><TableRow>
              <TH>Rank</TH>
              <TH>Group</TH>
              <TH>Hotels</TH>
              {COLS.filter(c=>cols[c.key]).map(c=><TH key={c.key} right>{"Avg "+c.label}</TH>)}
            </TableRow></TableHead>
            <TableBody>{grouped.map((g,i)=>(
              <TableRow key={g.group} className={cls.row} style={{background:g.myGroup?"#E6F4E8":"transparent"}}>
                <TD bold={g.myGroup} highlight={g.myGroup} mono>{i+1}</TD>
                <TD bold={g.myGroup} highlight={g.myGroup}>{g.group}{g.myGroup&&<span className={cls.youBadge}>YOU</span>}</TD>
                <TD mono>{g.count}</TD>
                {COLS.filter(c=>cols[c.key]).map(c=>{
                  const key="avg"+c.key.charAt(0).toUpperCase()+c.key.slice(1);
                  return <TD key={c.key} right bold={g.myGroup} highlight={g.myGroup} mono>{c.fmt(g[key])}</TD>;
                })}
              </TableRow>
            ))}</TableBody>
          </Table>
        </Card>
      )}

      {rankBy==="state"&&(
        <Card title="Ranking by State" subtitle="Average metrics per state -- ranked by DRIVO %">
          <Table className={cls.table}>
            <TableHead><TableRow>
              <TH>Rank</TH>
              <TH>State</TH>
              <TH>Hotels</TH>
              <TH>Your Rank</TH>
              {COLS.filter(c=>cols[c.key]).map(c=><TH key={c.key} right>{"Avg "+c.label}</TH>)}
            </TableRow></TableHead>
            <TableBody>{byState.map((s,i)=>(
              <TableRow key={s.state} className={cls.row} style={{background:s.myState?"#E6F4E8":"transparent"}}>
                <TD bold={s.myState} highlight={s.myState} mono>{i+1}</TD>
                <TD bold={s.myState} highlight={s.myState}>{s.state}{s.myState&&<span className={cls.youBadge}>YOU</span>}</TD>
                <TD mono>{s.count}</TD>
                <TD bold={s.myState} highlight={s.myState} mono>{s.myState?s.myRank+"/"+s.count:"--"}</TD>
                {COLS.filter(c=>cols[c.key]).map(c=>{
                  const key="avg"+c.key.charAt(0).toUpperCase()+c.key.slice(1);
                  return <TD key={c.key} right bold={s.myState} highlight={s.myState} mono>{c.fmt(s[key])}</TD>;
                })}
              </TableRow>
            ))}</TableBody>
          </Table>
        </Card>
      )}

    </Box>
  );
}
